// Web Push in the browser (docs/notifications/SPEC.md). On iPhone it works only in the app
// added to the Home Screen (iOS 16.4+): in a Safari tab there is no PushManager.
import axios from 'axios';

export function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

// iPhone in a Safari tab: push appears after «Add to Home Screen».
export function needsHomeScreen() {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
  return ios && !standalone;
}

export function permission() {
  return 'Notification' in window ? Notification.permission : 'denied';
}

function keyBytes(base64url) {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

export async function currentSubscription() {
  if (!isPushSupported()) {
    return null;
  }
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/**
 * Asks for permission (must run from a tap), subscribes this device and saves it on the server.
 * Throws { code: 'denied' } when the user refused notifications.
 */
export async function enablePush(vapidPublicKey) {
  const result = await Notification.requestPermission();
  if (result !== 'granted') {
    throw { code: 'denied' };
  }

  const registration = await navigator.serviceWorker.ready;
  // A subscription made with other VAPID keys cannot be reused: start clean.
  const old = await registration.pushManager.getSubscription();
  if (old) {
    await old.unsubscribe().catch(() => {});
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(vapidPublicKey),
  });

  const { data } = await axios.post('/user/push-subscriptions', subscription.toJSON());
  return { subscription, data };
}

export async function disablePush() {
  const subscription = await currentSubscription();
  if (!subscription) {
    return null;
  }
  const { endpoint } = subscription;
  await subscription.unsubscribe().catch(() => {});
  const { data } = await axios.delete('/user/push-subscriptions', { data: { endpoint } });
  return data;
}

// The device is subscribed in the browser: make sure the server has it (it could have been removed).
export async function syncSubscription(subscription) {
  const { data } = await axios.post('/user/push-subscriptions', subscription.toJSON());
  return data;
}

export async function sendTestPush(subscription) {
  const { data } = await axios.post('/user/push-subscriptions/test', { endpoint: subscription.endpoint });
  return data;
}
